import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
    Bell, Megaphone, Clock, CheckCircle, Info,
    Sparkles, RefreshCw, HelpCircle, Plus, Ticket,
    AlertCircle, X, Send, MessageSquare, Tag,
    ChevronRight, Search, ArrowLeft, Loader2,
    Phone, Mail, MapPin, ExternalLink
} from 'lucide-react';
import DashboardLayout from '../components/dashboard/DashboardLayout';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { useAuth, API } from '../App';
import axios from 'axios';
import { toast } from 'sonner';

const VendorAnnouncementsSupport = () => {
    const { token, user } = useAuth();
    const [activeTab, setActiveTab] = useState('announcements');

    // Announcements state
    const [announcements, setAnnouncements] = useState([]);
    const [announcementsLoading, setAnnouncementsLoading] = useState(true);
    const [readAnnouncements, setReadAnnouncements] = useState(() => {
        try {
            const saved = localStorage.getItem('read_announcements');
            return saved ? JSON.parse(saved) : [];
        } catch { return []; }
    });

    // Tickets state
    const [tickets, setTickets] = useState([]);
    const [ticketsLoading, setTicketsLoading] = useState(true);
    const [showCreateForm, setShowCreateForm] = useState(false);
    const [selectedTicket, setSelectedTicket] = useState(null);
    const [replyText, setReplyText] = useState('');
    const [sending, setSending] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');

    const [newTicket, setNewTicket] = useState({
        subject: '',
        message: '',
        category: 'general'
    });

    const categories = [
        { value: 'general', label: 'General Inquiry', icon: '💬' },
        { value: 'technical', label: 'Technical Issue', icon: '🔧' },
        { value: 'billing', label: 'Billing & Payments', icon: '💰' },
        { value: 'products', label: 'Products & Inventory', icon: '📦' },
        { value: 'orders', label: 'Orders & Shipping', icon: '🚚' }
    ];

    useEffect(() => {
        fetchAnnouncements();
        fetchTickets();
    }, []);

    // Announcements functions
    const fetchAnnouncements = async () => {
        setAnnouncementsLoading(true);
        try {
            const res = await axios.get(`${API}/api/vendor/announcements`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setAnnouncements(res.data);
        } catch (error) {
            console.error('Failed to fetch announcements:', error);
            setAnnouncements([]);
        }
        setAnnouncementsLoading(false);
    };

    const markAsRead = (id) => {
        const updated = [...readAnnouncements, id];
        setReadAnnouncements(updated);
        localStorage.setItem('read_announcements', JSON.stringify(updated));
    };

    const isRead = (id) => readAnnouncements.includes(id);

    // Tickets functions
    const fetchTickets = async () => {
        setTicketsLoading(true);
        try {
            const res = await axios.get(`${API}/api/vendor/tickets`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setTickets(res.data);
        } catch (error) {
            console.error('Failed to fetch tickets:', error);
            setTickets([]);
        }
        setTicketsLoading(false);
    };

    const createTicket = async () => {
        if (!newTicket.subject.trim() || !newTicket.message.trim()) {
            toast.error('Please fill in all fields');
            return;
        }

        setSending(true);
        try {
            await axios.post(`${API}/api/vendor/tickets`, newTicket, {
                headers: { Authorization: `Bearer ${token}` }
            });
            toast.success('Ticket created successfully!');
            setShowCreateForm(false);
            setNewTicket({ subject: '', message: '', category: 'general' });
            fetchTickets();
        } catch (error) {
            toast.error('Failed to create ticket');
        }
        setSending(false);
    };

    const sendReply = async () => {
        if (!replyText.trim()) return;

        setSending(true);
        try {
            await axios.post(
                `${API}/api/vendor/tickets/${selectedTicket.id}/reply`,
                { message: replyText },
                { headers: { Authorization: `Bearer ${token}` } }
            );
            toast.success('Reply sent!');
            setReplyText('');

            const res = await axios.get(`${API}/api/vendor/tickets/${selectedTicket.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setSelectedTicket(res.data);
            fetchTickets();
        } catch (error) {
            toast.error('Failed to send reply');
        }
        setSending(false);
    };

    const getTimeAgo = (dateString) => {
        const date = new Date(dateString);
        const now = new Date();
        const diff = Math.floor((now - date) / 1000);

        if (diff < 60) return 'Just now';
        const mins = Math.floor(diff / 60);
        if (diff < 3600) return `${mins} min${mins !== 1 ? 's' : ''} ago`;
        const hrs = Math.floor(diff / 3600);
        if (diff < 86400) return `${hrs} hour${hrs !== 1 ? 's' : ''} ago`;
        const days = Math.floor(diff / 86400);
        if (diff < 604800) return `${days} day${days !== 1 ? 's' : ''} ago`;
        return date.toLocaleDateString();
    };

    const getStatusConfig = (status) => {
        const configs = {
            open: { color: 'bg-blue-100 text-blue-700', icon: AlertCircle, label: 'Open' },
            in_progress: { color: 'bg-yellow-100 text-yellow-700', icon: Clock, label: 'In Progress' },
            resolved: { color: 'bg-green-100 text-green-700', icon: CheckCircle, label: 'Resolved' },
            closed: { color: 'bg-gray-100 text-gray-700', icon: X, label: 'Closed' }
        };
        return configs[status] || configs.open;
    };

    const unreadCount = announcements.filter(a => !isRead(a.id)).length;
    const openTicketsCount = tickets.filter(t => t.status === 'open' || t.status === 'in_progress').length;
    const filteredTickets = tickets.filter(t =>
        t.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
        t.message.toLowerCase().includes(searchTerm.toLowerCase())
    );

    return (
        <DashboardLayout userRole="vendor">
            <div className="space-y-6">
                {/* Header */}
                <div className="bg-gradient-to-r from-primary/10 via-blue-50 to-purple-50 rounded-2xl p-6 border">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                        <div>
                            <h1 className="text-2xl md:text-3xl font-bold flex items-center gap-2">
                                <HelpCircle className="w-8 h-8 text-primary" />
                                Announcements & Support
                            </h1>
                            <p className="text-muted-foreground mt-2">
                                Stay updated with the latest news and get help when you need it.
                                View important announcements from admin or raise a support ticket for any issues.
                            </p>
                        </div>
                        <Button onClick={() => { setActiveTab('support'); setShowCreateForm(true); }}>
                            <Plus className="w-4 h-4 mr-2" />
                            New Ticket
                        </Button>
                    </div>
                </div>

                {/* Contact Us Card */}
                <div className="bg-card rounded-xl border p-6">
                    <h3 className="font-semibold flex items-center gap-2 mb-4">
                        <Phone className="w-5 h-5 text-primary" />
                        Contact Us
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                        <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                            <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center">
                                <Phone className="w-5 h-5 text-green-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Phone Support</p>
                                <p className="font-semibold">+91 1800-XXX-XXXX</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                            <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                                <Mail className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Email Support</p>
                                <p className="font-semibold">vendor-support@solarsavers.in</p>
                            </div>
                        </div>
                        <div className="flex items-center gap-3 p-4 bg-muted/30 rounded-lg">
                            <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center">
                                <Clock className="w-5 h-5 text-purple-600" />
                            </div>
                            <div>
                                <p className="text-sm text-muted-foreground">Working Hours</p>
                                <p className="font-semibold">Mon-Sat, 9 AM - 6 PM</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Tab Switcher */}
                <div className="flex bg-muted rounded-lg p-1">
                    <button
                        onClick={() => setActiveTab('announcements')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${activeTab === 'announcements'
                            ? 'bg-white shadow-sm text-primary'
                            : 'text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <Bell className="w-4 h-4" />
                        Announcements
                        {unreadCount > 0 && (
                            <Badge className="bg-primary text-white text-xs">{unreadCount}</Badge>
                        )}
                    </button>
                    <button
                        onClick={() => setActiveTab('support')}
                        className={`flex-1 flex items-center justify-center gap-2 px-4 py-2.5 rounded-md font-medium transition-all ${activeTab === 'support'
                            ? 'bg-white shadow-sm text-primary'
                            : 'text-muted-foreground hover:text-foreground'
                            }`}
                    >
                        <Ticket className="w-4 h-4" />
                        My Tickets
                        {openTicketsCount > 0 && (
                            <Badge variant="secondary" className="text-xs">{openTicketsCount}</Badge>
                        )}
                    </button>
                </div>

                {/* Announcements Tab */}
                {activeTab === 'announcements' && (
                    <div className="space-y-4">
                        <div className="flex justify-end">
                            <Button variant="outline" size="sm" onClick={fetchAnnouncements}>
                                <RefreshCw className="w-4 h-4 mr-2" />
                                Refresh
                            </Button>
                        </div>

                        {announcementsLoading ? (
                            <div className="space-y-4">
                                {[1, 2].map(i => (
                                    <div key={i} className="bg-card rounded-xl border p-6 animate-pulse">
                                        <div className="h-5 bg-muted rounded w-1/3 mb-3"></div>
                                        <div className="h-4 bg-muted rounded w-full mb-2"></div>
                                        <div className="h-4 bg-muted rounded w-3/4"></div>
                                    </div>
                                ))}
                            </div>
                        ) : announcements.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-card rounded-xl border p-12 text-center"
                            >
                                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Megaphone className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-semibold text-lg">No Announcements</h3>
                                <p className="text-muted-foreground mt-2">
                                    There are no announcements at this time. Check back later!
                                </p>
                            </motion.div>
                        ) : (
                            <div className="space-y-4">
                                {announcements.map((announcement, index) => {
                                    const read = isRead(announcement.id);
                                    return (
                                        <motion.div
                                            key={announcement.id}
                                            initial={{ opacity: 0, y: 20 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className={`bg-card rounded-xl border p-6 transition-all hover:shadow-md cursor-pointer ${!read ? 'border-l-4 border-l-primary bg-primary/5' : ''
                                                }`}
                                            onClick={() => !read && markAsRead(announcement.id)}
                                        >
                                            <div className="flex items-start justify-between gap-4">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${!read ? 'bg-primary text-white' : 'bg-muted'
                                                            }`}>
                                                            {!read ? <Sparkles className="w-4 h-4" /> : <Info className="w-4 h-4" />}
                                                        </div>
                                                        <h3 className="font-semibold text-lg">{announcement.title}</h3>
                                                        {!read && (
                                                            <Badge className="bg-primary/20 text-primary text-xs">New</Badge>
                                                        )}
                                                    </div>
                                                    <p className="text-muted-foreground whitespace-pre-wrap">
                                                        {announcement.message}
                                                    </p>
                                                    <div className="flex items-center gap-4 mt-4 text-sm text-muted-foreground">
                                                        <span className="flex items-center gap-1">
                                                            <Clock className="w-4 h-4" />
                                                            {getTimeAgo(announcement.created_at)}
                                                        </span>
                                                        <span>From: {announcement.created_by || 'Admin'}</span>
                                                    </div>
                                                </div>
                                                {read && (
                                                    <div className="flex items-center gap-1 text-green-600 text-sm">
                                                        <CheckCircle className="w-4 h-4" />
                                                        Read
                                                    </div>
                                                )}
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Support Tab */}
                {activeTab === 'support' && (
                    <div className="space-y-4">
                        {/* Stats */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                            <div className="bg-card rounded-xl border p-4">
                                <p className="text-sm text-muted-foreground">Total Tickets</p>
                                <p className="text-2xl font-bold mt-1">{tickets.length}</p>
                            </div>
                            <div className="bg-card rounded-xl border p-4">
                                <p className="text-sm text-muted-foreground">Open</p>
                                <p className="text-2xl font-bold text-blue-600 mt-1">
                                    {tickets.filter(t => t.status === 'open').length}
                                </p>
                            </div>
                            <div className="bg-card rounded-xl border p-4">
                                <p className="text-sm text-muted-foreground">In Progress</p>
                                <p className="text-2xl font-bold text-yellow-600 mt-1">
                                    {tickets.filter(t => t.status === 'in_progress').length}
                                </p>
                            </div>
                            <div className="bg-card rounded-xl border p-4">
                                <p className="text-sm text-muted-foreground">Resolved</p>
                                <p className="text-2xl font-bold text-green-600 mt-1">
                                    {tickets.filter(t => t.status === 'resolved' || t.status === 'closed').length}
                                </p>
                            </div>
                        </div>

                        {/* Search */}
                        <div className="relative">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
                            <input
                                type="text"
                                placeholder="Search tickets..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className="w-full pl-10 pr-4 py-2.5 bg-card border rounded-lg focus:ring-2 ring-primary/20 focus:border-primary"
                            />
                        </div>

                        {/* Tickets List */}
                        {ticketsLoading ? (
                            <div className="space-y-4">
                                {[1, 2].map(i => (
                                    <div key={i} className="bg-card rounded-xl border p-5 animate-pulse">
                                        <div className="h-4 bg-muted rounded w-1/4 mb-2"></div>
                                        <div className="h-6 bg-muted rounded w-3/4"></div>
                                    </div>
                                ))}
                            </div>
                        ) : filteredTickets.length === 0 ? (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                className="bg-card rounded-xl border p-12 text-center"
                            >
                                <div className="w-16 h-16 bg-muted rounded-full flex items-center justify-center mx-auto mb-4">
                                    <Ticket className="w-8 h-8 text-muted-foreground" />
                                </div>
                                <h3 className="font-semibold text-lg">No Tickets</h3>
                                <p className="text-muted-foreground mt-2 mb-4">
                                    {searchTerm ? 'No tickets match your search' : 'You haven\'t created any support tickets yet'}
                                </p>
                                <Button onClick={() => setShowCreateForm(true)}>
                                    <Plus className="w-4 h-4 mr-2" />
                                    Create Your First Ticket
                                </Button>
                            </motion.div>
                        ) : (
                            <div className="space-y-3">
                                {filteredTickets.map((ticket, index) => {
                                    const config = getStatusConfig(ticket.status);
                                    return (
                                        <motion.div
                                            key={ticket.id}
                                            initial={{ opacity: 0, y: 10 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            transition={{ delay: index * 0.05 }}
                                            className="bg-card rounded-xl border p-5 hover:shadow-md transition-all cursor-pointer"
                                            onClick={() => setSelectedTicket(ticket)}
                                        >
                                            <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2 mb-1">
                                                        <Badge className={config.color}>{config.label}</Badge>
                                                        <Badge variant="outline">{categories.find(c => c.value === ticket.category)?.label || ticket.category}</Badge>
                                                    </div>
                                                    <h3 className="font-semibold">{ticket.subject}</h3>
                                                    <p className="text-sm text-muted-foreground line-clamp-1 mt-1">{ticket.message}</p>
                                                </div>
                                                <div className="flex items-center gap-4">
                                                    <div className="text-right">
                                                        <p className="text-sm text-muted-foreground">
                                                            {new Date(ticket.created_at).toLocaleDateString()}
                                                        </p>
                                                        <p className="text-xs text-muted-foreground flex items-center gap-1 justify-end">
                                                            <MessageSquare className="w-3 h-3" />
                                                            {ticket.replies?.length || 0} replies
                                                        </p>
                                                    </div>
                                                    <ChevronRight className="w-5 h-5 text-muted-foreground" />
                                                </div>
                                            </div>
                                        </motion.div>
                                    );
                                })}
                            </div>
                        )}
                    </div>
                )}

                {/* Create Ticket Modal */}
                <AnimatePresence>
                    {showCreateForm && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                            onClick={() => setShowCreateForm(false)}
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b">
                                    <h2 className="text-xl font-bold flex items-center gap-2">
                                        <Plus className="w-5 h-5 text-primary" />
                                        Create Support Ticket
                                    </h2>
                                </div>

                                <div className="p-6 space-y-4">
                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Category</label>
                                        <div className="grid grid-cols-2 gap-2">
                                            {categories.map(cat => (
                                                <button
                                                    key={cat.value}
                                                    onClick={() => setNewTicket({ ...newTicket, category: cat.value })}
                                                    className={`p-3 rounded-lg border text-left transition-all ${newTicket.category === cat.value
                                                        ? 'border-primary bg-primary/10'
                                                        : 'hover:border-primary/50'
                                                        }`}
                                                >
                                                    <span className="text-lg mr-2">{cat.icon}</span>
                                                    <span className="text-sm font-medium">{cat.label}</span>
                                                </button>
                                            ))}
                                        </div>
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Subject</label>
                                        <input
                                            type="text"
                                            placeholder="Brief description of your issue"
                                            value={newTicket.subject}
                                            onChange={(e) => setNewTicket({ ...newTicket, subject: e.target.value })}
                                            className="w-full p-3 border rounded-lg focus:ring-2 ring-primary/20 focus:border-primary"
                                        />
                                    </div>

                                    <div>
                                        <label className="text-sm font-medium mb-2 block">Message</label>
                                        <Textarea
                                            placeholder="Describe your issue in detail..."
                                            value={newTicket.message}
                                            onChange={(e) => setNewTicket({ ...newTicket, message: e.target.value })}
                                            rows={5}
                                        />
                                    </div>
                                </div>

                                <div className="p-6 border-t flex justify-end gap-3">
                                    <Button variant="outline" onClick={() => setShowCreateForm(false)}>Cancel</Button>
                                    <Button onClick={createTicket} disabled={sending}>
                                        {sending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                                        Submit Ticket
                                    </Button>
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>

                {/* Ticket Detail Modal */}
                <AnimatePresence>
                    {selectedTicket && (
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
                            onClick={() => setSelectedTicket(null)}
                        >
                            <motion.div
                                initial={{ scale: 0.9 }}
                                animate={{ scale: 1 }}
                                exit={{ scale: 0.9 }}
                                className="bg-card rounded-2xl shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto"
                                onClick={(e) => e.stopPropagation()}
                            >
                                <div className="p-6 border-b sticky top-0 bg-card z-10">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-3">
                                            <button onClick={() => setSelectedTicket(null)} className="p-1 hover:bg-muted rounded">
                                                <ArrowLeft className="w-5 h-5" />
                                            </button>
                                            <div>
                                                <h2 className="text-lg font-bold">{selectedTicket.subject}</h2>
                                                <div className="flex items-center gap-2 mt-1">
                                                    <Badge className={getStatusConfig(selectedTicket.status).color}>
                                                        {getStatusConfig(selectedTicket.status).label}
                                                    </Badge>
                                                    <span className="text-sm text-muted-foreground">
                                                        #{String(selectedTicket.id).slice(0, 8)}
                                                    </span>
                                                </div>
                                            </div>
                                        </div>
                                        <Button variant="ghost" size="icon" onClick={() => setSelectedTicket(null)}>
                                            <X className="w-5 h-5" />
                                        </Button>
                                    </div>
                                </div>

                                <div className="p-6 space-y-6">
                                    <div className="bg-muted/30 rounded-xl p-4">
                                        <div className="flex items-center gap-2 mb-2">
                                            <div className="w-8 h-8 bg-primary/10 rounded-full flex items-center justify-center text-primary font-semibold">
                                                {user?.name?.[0] || 'V'}
                                            </div>
                                            <div>
                                                <p className="font-medium text-sm">{user?.name || 'You'}</p>
                                                <p className="text-xs text-muted-foreground">
                                                    {new Date(selectedTicket.created_at).toLocaleString()}
                                                </p>
                                            </div>
                                        </div>
                                        <p className="whitespace-pre-wrap">{selectedTicket.message}</p>
                                    </div>

                                    {selectedTicket.replies?.length > 0 && (
                                        <div className="space-y-3">
                                            <p className="font-medium flex items-center gap-2">
                                                <MessageSquare className="w-4 h-4" />
                                                Conversation ({selectedTicket.replies.length})
                                            </p>
                                            {selectedTicket.replies.map((reply, idx) => (
                                                <div
                                                    key={idx}
                                                    className={`p-4 rounded-xl ${reply.is_admin
                                                        ? 'bg-primary/10 border-l-4 border-l-primary'
                                                        : 'bg-muted/30'
                                                        }`}
                                                >
                                                    <div className="flex items-center gap-2 mb-2">
                                                        <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-semibold ${reply.is_admin ? 'bg-primary text-white' : 'bg-muted'
                                                            }`}>
                                                            {reply.user_name?.[0] || '?'}
                                                        </div>
                                                        <span className="font-medium text-sm">{reply.user_name}</span>
                                                        {reply.is_admin && <Badge className="text-xs">Support</Badge>}
                                                        <span className="text-xs text-muted-foreground">
                                                            {new Date(reply.created_at).toLocaleString()}
                                                        </span>
                                                    </div>
                                                    <p className="text-sm whitespace-pre-wrap">{reply.message}</p>
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {selectedTicket.status !== 'closed' && (
                                        <div className="space-y-3 pt-4 border-t">
                                            <Textarea
                                                placeholder="Write your reply..."
                                                value={replyText}
                                                onChange={(e) => setReplyText(e.target.value)}
                                                rows={3}
                                            />
                                            <Button onClick={sendReply} disabled={!replyText.trim() || sending}>
                                                {sending ? (
                                                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                                                ) : (
                                                    <Send className="w-4 h-4 mr-2" />
                                                )}
                                                Send Reply
                                            </Button>
                                        </div>
                                    )}

                                    {selectedTicket.status === 'closed' && (
                                        <div className="bg-muted rounded-lg p-4 text-center">
                                            <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                                            <p className="font-medium">This ticket is closed</p>
                                            <p className="text-sm text-muted-foreground">Create a new ticket if you need further assistance</p>
                                        </div>
                                    )}
                                </div>
                            </motion.div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </div>
        </DashboardLayout>
    );
};

export default VendorAnnouncementsSupport;
